import type { ReactNode } from 'react';
import { Spinner } from '@heroui/react';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { Tabs, type TabItem } from '@/components/ui/Tabs';

export interface WorkflowLink { key: string; label: string; icon: ReactNode; to: string }

/**
 * The reusable, entity-agnostic Investigation Drawer. Wraps the design-system
 * Drawer + Tabs and renders a chips header, a "Investigate in" workflow quick-nav
 * and a tab set supplied by the caller. The attachment and URL investigation
 * features both compose this — one drawer, no duplication. Loading / empty /
 * ready states are driven by `isLoading` and the presence of `tabs`.
 */
export function InvestigationDrawer({
  isOpen, onClose, title, width = 'w-full max-w-3xl', footer,
  isLoading = false, loadingLabel = 'Loading investigation…',
  header, workflow, tabs, onNavigate, emptyMessage = 'Investigation data unavailable.',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  width?: string;
  footer?: ReactNode;
  isLoading?: boolean;
  loadingLabel?: string;
  header?: ReactNode;
  workflow?: WorkflowLink[];
  tabs?: TabItem[];
  onNavigate?: (to: string) => void;
  emptyMessage?: string;
}) {
  return (
    <Drawer isOpen={isOpen} onClose={onClose} width={width} title={title} footer={footer}>
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner label={loadingLabel} /></div>
      ) : tabs ? (
        <div className="space-y-4">
          {header && <div className="flex flex-wrap items-center gap-2">{header}</div>}
          {workflow && workflow.length > 0 && (
            <div className="flex flex-wrap gap-2 rounded-lg border border-border bg-surface-raised/40 p-2">
              <span className="self-center pl-1 text-xs font-medium text-fg-subtle">Investigate in:</span>
              {workflow.map((w) => (
                <Button key={w.key} size="sm" variant="flat" startContent={w.icon} onPress={() => onNavigate?.(w.to)}>{w.label}</Button>
              ))}
            </div>
          )}
          <Tabs ariaLabel={typeof title === 'string' ? title : 'Investigation'} items={tabs} />
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-fg-subtle">{emptyMessage}</p>
      )}
    </Drawer>
  );
}
